import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { getPresentExpos } from "../Apis/ExpoAPIs";
import { getStallTypes } from "../Apis/StallTypeAPIs";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { lookupIdCard, downloadPass } from "../Apis/IdCardAPIs";
import { Modal, Button } from "react-bootstrap";

const IdCardLayer = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    phone: "",
    name: "",
    email: "",
    role: "",
    idNo: "",
    stallNo: "",
    cardType: "Free", // Stall, Paid, Free, Corner
    cardColor: "#007bff", // Default Blue
    expoId: "",
    stallTypeId: "",
    qrCode: "",
    attendance: false,
    attendedDate: null,
    attendedTime: null,
    knowAboutExhibition: "",
  });

  const [expos, setExpos] = useState([]);
  const [stallTypes, setStallTypes] = useState([]);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const pdfPreviewUrlRef = useRef(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [includeTemplate, setIncludeTemplate] = useState(false);

  // Debounced effect to fetch PDF for preview
  useEffect(() => {
    const fetchPdfPreview = async () => {
      if (!formData.phone || !formData.expoId || formData.phone.length !== 10 || !formData.name) {
        setPdfPreviewUrl(null);
        return;
      }
      
      setIsLoadingPdf(true);
      try {
        const type = formData.cardType === "Stall" ? "stall" : "visitor";
        const res = await downloadPass({
          mobileNo: formData.phone,
          expoId: formData.expoId,
          type: type,
          showTemplate: includeTemplate
        });
        
        if (res.status && res.response) {
          const url = window.URL.createObjectURL(new Blob([res.response], { type: 'application/pdf' }));
          pdfPreviewUrlRef.current = url;
          setPdfPreviewUrl(url);
        } else {
          setPdfPreviewUrl(null);
        }
      } catch (err) {
        console.error("Preview generation error:", err);
        setPdfPreviewUrl(null);
      } finally {
        setIsLoadingPdf(false);
      }
    };
    
    // Clear previous URL to avoid memory leaks (use ref to avoid adding pdfPreviewUrl as a dep)
    if (pdfPreviewUrlRef.current) {
      window.URL.revokeObjectURL(pdfPreviewUrlRef.current);
      pdfPreviewUrlRef.current = null;
    }
    
    const timeoutId = setTimeout(() => {
      fetchPdfPreview();
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [formData.phone, formData.expoId, formData.cardType, formData.name, includeTemplate]);

  useEffect(() => {
    const fetchData = async () => {
      const [expoRes, stallRes] = await Promise.all([
        getPresentExpos(),
        getStallTypes(),
      ]);

      if (expoRes.status) {
        const data = expoRes.response?.data || expoRes.response || [];
        setExpos(
          data.map((expo) => ({
            value: expo._id || expo.id,
            label: expo.expoName || expo.name || expo.title,
          })),
        );
      }

      if (stallRes.status) {
        const data = stallRes.response?.data || stallRes.response || [];
        setStallTypes(
          data.map((type) => ({
            value: type._id || type.id,
            label: type.stallTypeName || type.name,
          })),
        );
      }
    };
    fetchData();
  }, []);

  // Deep link support from Reports
  useEffect(() => {
    if (location.state?.phone) {
      const cleanedPhone = String(location.state.phone).replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: cleanedPhone }));
    }
  }, [location.state]);

  const fillStallDetails = (data) => {
    // Try to find the stall type ID by name if it's not directly provided as an ID
    let matchedStallTypeId = data.stallTypeId?._id || data.stallTypeId;

    if (!matchedStallTypeId && data.stallType) {
      const found = stallTypes.find(
        (t) => t.label.toLowerCase() === data.stallType.toLowerCase(),
      );
      if (found) matchedStallTypeId = found.value;
    }

    setFormData((prev) => ({
      ...prev,
      name: data.name || data.companyName || "",
      email: data.email || "",
      role: "EXHIBITOR",
      idNo: data._id || data.id || "",
      stallNo: data.stallNo || "",
      cardType: "Stall",
      cardColor: "#ed1c24", // Red
      stallTypeId: matchedStallTypeId || prev.stallTypeId,
      qrCode: data.qrCode || "",
    }));
    toast.success("Exhibitor Record Loaded!");
  };

  const fillVisitorDetails = (data, entryType) => {
    const isPaid =
      entryType === "Paid" || data.entryType === "Paid" || data.expoStatus === "Booked" || data.paymentStatus === "Paid";
    setFormData((prev) => ({
      ...prev,
      name: data.name || "",
      email: data.email || "",
      role: isPaid ? "PAID VISITOR" : "FREE VISITOR",
      idNo: data._id || data.id || "",
      cardType: isPaid ? "Paid" : "Free",
      cardColor: isPaid ? "#d4af37" : "#007bff", // Gold for Paid, Blue for Free
      stallNo: "",
      stallTypeId: "",
      qrCode: data.qrCode || "",
      attendance: data.attendance || "Not Attended",
      attendedDate: data.attendedDate || null,
      attendedTime: data.attendedTime || null,
      knowAboutExhibition: data.knowAboutExhibition || "",
    }));
    toast.success(`${isPaid ? "Paid" : "Free"} Visitor Record Loaded!`);
  };

  const performLookup = async (phone, expoId) => {
    if (!phone || phone.length !== 10 || !expoId) return;

    setIsLookingUp(true);
    try {
      const res = await lookupIdCard({
        mobileNo: phone,
        expoId: expoId,
      });

      if (res.status && res.response?.data) {
        const { isExhibitor, isVisitor, stallDetails, visitorDetails, entryType } =
          res.response.data;

        if (isExhibitor && isVisitor) {
          setLookupResults({ stallDetails, visitorDetails, entryType });
          setShowSelectionModal(true);
        } else if (isExhibitor) {
          fillStallDetails(stallDetails);
        } else if (isVisitor) {
          fillVisitorDetails(visitorDetails, entryType);
        } else {
          toast.error("No record found for this number in selected expo.");
        }
      } else {
        toast.error("No record found for this number in selected expo.");
      }
    } catch (err) {
      console.error("Lookup error:", err);
      toast.error("Lookup failed");
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    let cleanedValue = value;
    if (name === "phone") {
      cleanedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => {
        const newData = { ...prev, [name]: cleanedValue };
        if (name === "phone" && cleanedValue.length === 10) {
            if (!prev.expoId) {
                toast.warning("Please select an Expo first for lookup");
            } else {
                performLookup(cleanedValue, prev.expoId);
            }
        }
        return newData;
    });
  };

  const handleSelectChoice = (choice) => {
    if (choice === "stall") {
      fillStallDetails(lookupResults.stallDetails);
      setFormData((prev) => ({ ...prev, cardType: "Stall" }));
    } else {
      fillVisitorDetails(lookupResults.visitorDetails, lookupResults.entryType);
      setFormData((prev) => ({ ...prev, cardType: "Free" })); // Visitor default
    }
    setShowSelectionModal(false);
  };

  const handleDownload = async () => {
    if (!pdfPreviewUrl) {
      toast.warning("Please look up a valid mobile number first and wait for the preview.");
      return;
    }
    
    try {
      const type = formData.cardType === "Stall" ? "stall" : "visitor";
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.setAttribute('download', `${type}_Pass_${(formData.name || 'User').replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("ID Card Downloaded!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download");
    }
  };

  // const qrUrl =
  //   formData.qrCode ||
  //   `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`Name: ${formData.name}, Email: ${formData.email}`)}`;

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000, #bb0007, #ff0000)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">ID Card Generator</h6>
        <button
          className="btn btn-sm btn-light d-flex align-items-center gap-2 px-16 shadow-sm fw-bold"
          onClick={handleDownload}
          style={{ color: "#ed1c24" }}
        >
          <Icon icon="solar:download-bold" />
          Download ID Card
        </button>
      </div>

      <div className="card-body p-24">
        <div className="row gy-4">
          {/* Form Section */}
          <div className="col-lg-5">
            <div className="bg-light p-24 radius-12 border h-100">
              <div className="col-12 mb-16">
                <label className="form-label fw-bold small text-secondary">
                  PRESENT EXPO
                </label>
                <Select
                  options={expos}
                  styles={selectStyles()}
                  menuPortalTarget={document.body}
                  value={expos.find((opt) => opt.value === formData.expoId)}
                  onChange={(opt) => {
                    setFormData((prev) => ({ ...prev, expoId: opt.value }));
                    if (formData.phone && formData.phone.length === 10) {
                      performLookup(formData.phone, opt.value);
                    }
                  }}
                  placeholder="Select Present Expo"
                />
              </div>

              <div className="row gy-3 text-start">
                <div className="col-12">
                  <label className="form-label fw-bold small text-secondary">
                    MOBILE NO
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      name="phone"
                      className="form-control radius-8 border-primary pe-40"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter Phone to Lookup"
                      maxLength="10"
                      inputMode="numeric"
                    />
                    {isLookingUp && (
                      <div className="position-absolute end-0 top-50 translate-middle-y me-12">
                        <Icon
                          icon="line-md:loading-twotone-loop"
                          className="text-primary text-xl"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {formData.name && (
                  <div className="col-12 mt-12">
                    <div className="p-12 radius-8 bg-white border shadow-xs text-start">
                      <div className="d-flex align-items-center justify-content-between mb-8">
                        <span className="small text-secondary fw-semibold">Name</span>
                        <span className="small text-dark fw-bold">{formData.name}</span>
                      </div>
                      {formData.knowAboutExhibition && (
                        <div className="d-flex align-items-center justify-content-between mb-8">
                          <span className="small text-secondary fw-semibold">Known Source</span>
                          <span className="badge bg-info-subtle text-info fw-bold">{formData.knowAboutExhibition}</span>
                        </div>
                      )}
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="small text-secondary fw-semibold">Attendance</span>
                        <span className={`badge ${(formData.attendance === true || formData.attendance === 'Attended' || formData.attendance === 'true') ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} fw-bold`}>
                          {(formData.attendance === true || formData.attendance === 'Attended' || formData.attendance === 'true')
                            ? `✓ Attended ${formData.attendedDate ? `(${formData.attendedDate} ${formData.attendedTime || ''})` : ''}`
                            : '⏱ Not Attended'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-12 mt-16 pt-16 border-top">
                  <div className="d-flex align-items-center gap-3 p-12 radius-8 border bg-white shadow-xs">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="includeTemplateSwitch"
                        checked={includeTemplate}
                        onChange={(e) => setIncludeTemplate(e.target.checked)}
                        style={{ cursor: "pointer", width: "2.4em", height: "1.2em" }}
                      />
                    </div>
                    <label
                      className="form-check-label small fw-bold text-dark cursor-pointer mb-0 text-start"
                      htmlFor="includeTemplateSwitch"
                      style={{ userSelect: "none" }}
                    >
                      Show & Download Background Design
                      <span className="d-block small text-secondary fw-normal">
                        {includeTemplate ? "Template background active" : "Clean white for pre-printed badges"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="col-lg-7 d-flex align-items-center justify-content-center bg-base radius-12 border py-40">
            {isLoadingPdf ? (
               <div className="text-secondary text-center">
                 <div className="spinner-border mb-3" role="status">
                   <span className="visually-hidden">Loading...</span>
                 </div>
                 <p>Generating PDF Preview...</p>
               </div>
            ) : pdfPreviewUrl ? (
               <iframe 
                 src={pdfPreviewUrl} 
                 title="ID Card Preview"
                 style={{ width: "100%", height: "600px", border: "none", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
               />
            ) : (
               <div className="text-secondary text-center">
                 <Icon icon="solar:document-text-line-duotone" width="48" className="mb-2 opacity-50" />
                 <p>Enter a valid mobile number to view the pass preview</p>
               </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection Modal for Multiple Records */}
      <Modal
        show={showSelectionModal}
        onHide={() => setShowSelectionModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="text-dark fw-bold h6 mb-0">
            Multiple Records Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24 text-center">
          <p className="text-secondary mb-20">
            This mobile number is registered as both an Exhibitor and a Visitor.
            Which details would you like to use?
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Button
              variant="danger"
              className="px-24 radius-8 fw-bold"
              onClick={() => handleSelectChoice("stall")}
            >
              <Icon icon="solar:shop-bold" className="me-2" />
              Exhibitor
            </Button>
            <Button
              variant="primary"
              className="px-24 radius-8 fw-bold"
              onClick={() => handleSelectChoice("visitor")}
            >
              <Icon icon="solar:user-bold" className="me-2" />
              Visitor
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default IdCardLayer;
